/**
 * Category-aware income/expense flow rules for Financial Summary reporting.
 * Aligns with dashboard income (income category tree) and spending (debits excluding
 * transfer categories and exclude_from_spending, including parent exclusion).
 */

import type { ReportTransactionAmountLike } from './transaction-amount-utils';
import { isExpenseOutflow, isIncomeInflow } from './transaction-amount-utils';

export interface ReportingCategoryMeta {
  type: string | null;
  /** True when this category or an ancestor has type income (Income tree). */
  inIncomeTree: boolean;
  excludeFromSpending: boolean;
  parentExcludeFromSpending: boolean;
}

export interface ReportingCategoryRecord {
  id: string;
  type?: string | null;
  exclude_from_spending?: boolean;
  parent_id?: string | null;
}

export interface TransactionWithCategory extends ReportTransactionAmountLike {
  category_id?: string | null;
}

function normaliseCategoryType(type: string | null | undefined): string | null {
  if (!type) return null;
  return type.toLowerCase();
}

function categoryInIncomeTree(
  categoryId: string,
  byId: Map<string, ReportingCategoryRecord>
): boolean {
  let current: ReportingCategoryRecord | undefined = byId.get(categoryId);
  const visited = new Set<string>();
  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    if (normaliseCategoryType(current.type) === 'income') return true;
    current = current.parent_id ? byId.get(current.parent_id) : undefined;
  }
  return false;
}

export function buildReportingCategoryMetaMap(
  categories: ReportingCategoryRecord[]
): Map<string, ReportingCategoryMeta> {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const metaMap = new Map<string, ReportingCategoryMeta>();

  for (const category of categories) {
    const parent = category.parent_id ? byId.get(category.parent_id) : undefined;
    metaMap.set(category.id, {
      type: category.type ?? null,
      inIncomeTree: categoryInIncomeTree(category.id, byId),
      excludeFromSpending: Boolean(category.exclude_from_spending),
      parentExcludeFromSpending: Boolean(parent?.exclude_from_spending),
    });
  }

  return metaMap;
}

/** Transfer categories — not real income (exclude_from_spending does not apply to income). */
export function isExcludedFromIncomeFlows(meta: ReportingCategoryMeta | undefined): boolean {
  if (!meta) return false;
  return normaliseCategoryType(meta.type) === 'transfer';
}

/** Transfer categories and exclude-from-spending (incl. parent) — not real expense. */
export function isExcludedFromExpenseFlows(meta: ReportingCategoryMeta | undefined): boolean {
  if (!meta) return false;
  if (normaliseCategoryType(meta.type) === 'transfer') return true;
  if (meta.excludeFromSpending || meta.parentExcludeFromSpending) return true;
  return false;
}

/** @deprecated Use isExcludedFromExpenseFlows — kept for clarity in older references. */
export function isExcludedFromFinancialFlows(meta: ReportingCategoryMeta | undefined): boolean {
  return isExcludedFromExpenseFlows(meta);
}

export function isFinancialSummaryIncomeInflow(
  tx: TransactionWithCategory,
  metaMap: Map<string, ReportingCategoryMeta>
): boolean {
  const txType = (tx.type ?? '').toLowerCase();
  if (txType === 'transfer') return false;
  if (!tx.category_id) return false;
  if (!isIncomeInflow(tx)) return false;

  const meta = metaMap.get(tx.category_id);
  if (isExcludedFromIncomeFlows(meta)) return false;

  return Boolean(meta?.inIncomeTree);
}

export function isFinancialSummaryExpenseOutflow(
  tx: TransactionWithCategory,
  metaMap: Map<string, ReportingCategoryMeta>
): boolean {
  const txType = (tx.type ?? '').toLowerCase();
  if (txType === 'transfer') return false;

  const meta = tx.category_id ? metaMap.get(tx.category_id) : undefined;
  if (isExcludedFromExpenseFlows(meta)) return false;
  if (meta?.inIncomeTree) return false;

  return isExpenseOutflow(tx);
}

export function financialSummaryIncomeAmount(
  tx: TransactionWithCategory,
  metaMap: Map<string, ReportingCategoryMeta>
): number {
  if (!isFinancialSummaryIncomeInflow(tx, metaMap)) return 0;
  return Math.abs(Number(tx.amount) || 0);
}

export function financialSummaryExpenseAmount(
  tx: TransactionWithCategory,
  metaMap: Map<string, ReportingCategoryMeta>
): number {
  if (!isFinancialSummaryExpenseOutflow(tx, metaMap)) return 0;
  return Math.abs(Number(tx.amount) || 0);
}
